import { BACKGAMMON_SHORT_MATCH_LENGTH } from "../dto/table";
import type { OnlineTableReport, TableState } from "../dto/table";

/**
 * WHAT A TABLE ACTUALLY IS, IN PERSIAN — the shared lines both invitations are built from.
 *
 * Two very different surfaces tell a friend about the same table:
 *   • the BOT, when someone taps a shared `t.me/…?start=t_<game>_<roomId>` link (private tables);
 *   • the API, in the Telegram PV an OFFLINE invitee gets instead of the in-app card (public «+»
 *     seat invites AND private friend-list invites alike — measured, that PV is the majority path).
 *
 * They live in different services and are sent by different code, so the description belongs in
 * exactly ONE place or the two drift — the same reasoning that put the lockout sentence in
 * `leave-penalty.ts` beside the arithmetic that decides it. This returns only the SHAPE lines; each
 * caller writes its own opening (the bot names the HOST, the api names the INVITER) and its own
 * «برای ورود روی دکمهٔ زیر بزن», because only they know who is being named.
 *
 * ⚠️ IT SAYS ONLY WHAT THE REPORT PROVES. Every field is optional — the game-server and the two
 * readers deploy independently, and a table older than its 20s key is unreadable entirely — so a
 * missing value costs its LINE and nothing else. There is deliberately no branch that guesses a
 * default: «بازی تا ۷ دست» is true of every public حکم table today, and printing it from a
 * hardcoded assumption rather than from the table's own settings is exactly how a caption starts
 * lying the day a room type changes.
 */
export function describeTableShape(report: OnlineTableReport): string[] {
  return [tableKindLine(report), ...settingLines(report)];
}

/**
 * WHAT KIND OF TABLE IS THIS — a friend's own, or a room of strangers, and WHICH room.
 *
 * The first thing an invitation has to answer and the one thing it did not: it named the game and
 * its settings, but «یه میز «حکم» منتظرته» is the same sentence for a friend's private table and
 * for a public queue. Two of the games have TWO public rooms, so «public» alone does not finish
 * the answer either.
 *
 * It sits FIRST, above the settings, because it frames them — «بازی تا ۳ دست» means something
 * different on a table someone built for you than in a queue you are about to be matched into.
 * And it is here, in the SHARED describer, rather than in either caller's opening line: both
 * invitations render this block ({@link tableInviteBody}), while the openings genuinely differ
 * (the bot names the HOST, the api names the INVITER), so this is the only place that says it
 * once for both.
 *
 * ⚠️ ALWAYS A LINE. Unlike every other line here this one is never omitted — `private` is a
 * REQUIRED field of the report (a table that is readable at all has it), so there is nothing to
 * degrade to. The consequence is deliberate: a private بی‌دل/پاسور/بلوف table, which publishes no
 * settings of its own, now earns the «اطلاعات میز» heading + blockquote it never used to get.
 *
 * The wordings are the ones the player already knows from the lobby's own room chooser
 * («اتاق عمومی» / «اتاق عمومی پیشرفته» / «اتاق عمومی کوتاه») and from «میز خصوصی», so the blurb
 * read before joining and the caption read after being invited name the same room the same way.
 */
function tableKindLine(report: OnlineTableReport): string {
  if (report.private) return "🔒 میز خصوصی";
  // حکم's second room differs by RULES — which calls the Hakem may make IS what you are playing.
  if (report.game === "hokm" && report.hokmModes === true) return "🌐 اتاق عمومی پیشرفته";
  // تخته نرد's second room differs by LENGTH, and that is not a setting on a public table: a
  // room's name IS its matchmaking queue, so «۳ or ۵ دست» had to be two rooms. `publicMatchTo` is
  // a define()-time fact that `normalizeSettings` FORCES over anything a client sends, so on a
  // PUBLIC table the published length identifies the queue exactly — this reads the room off a
  // fact, it does not guess one. An absent length (a game-server predating the field) still says
  // «اتاق عمومی», which is true of both; naming one of the two would not be.
  if (report.game === "backgammon" && report.matchLength === BACKGAMMON_SHORT_MATCH_LENGTH)
    return "🌐 اتاق عمومی کوتاه";
  return "🌐 اتاق عمومی";
}

/** The game's own settings, in its own vocabulary. Empty when the game has nothing to choose. */
function settingLines(report: OnlineTableReport): string[] {
  switch (report.game) {
    case "hokm":
      return hokmLines(report);
    case "backgammon":
      // Its length counts POINTS (مارس pays 2, سگ‌مارس 3) where حکم counts rounds, but Persian
      // names both «دست» and the reader already knows which game it is.
      //
      // ⚠️ AND THAT IS ALL تخته نرد HAS. It has no کوت — مارس/سگ‌مارس are rules, identical on every
      // table, never a host setting — so there is no second line to write here, and inventing one
      // would put a choice on the card that the game does not offer.
      return matchLengthLine(report);
    case "mensch":
      // The ONE thing a منچ host chooses. Named for public tables too (always 4) — «چند نفره» is
      // the first thing anyone asks about منچ, and it is a fact about the table either way.
      return typeof report.seats === "number" ? [`👥 میز ${faNum(report.seats)} نفره`] : [];
    case "ladders":
      // مار پله comes in two sizes on BOTH doors (public queues for 2 and 4, a private host picks
      // 2 or 4), so — like منچ — the size is named on every table: it is what an invitee asks first.
      return typeof report.seats === "number" ? [`👥 میز ${faNum(report.seats)} نفره`] : [];
    case "chess":
      return typeof report.chessTimeControl === "string"
        ? [`⏱ ${chessTimeControlLabel(report.chessTimeControl)}`]
        : [];
    case "pasur":
      // پاسور has TWO sizes since 2026-10-20 (`pasur2-*`), and the table for two is the one an
      // invitee cannot assume (card-ps B1's deferred Q4; the operator, 2026-10-21: «add «۲ نفره» to
      // the invites»). The four-seat table — the size everyone knows — keeps saying nothing.
      return report.seats === 2 ? [`👥 میز ${faNum(2)} نفره`] : [];
    case "dooz":
      // دوز counts دست like حکم does, so it borrows the same line — the reader already knows
      // which game it is. It has nothing else a host chooses that is worth a line before anyone
      // has sat down (colour is per-VIEWER and the turn timer is one of the four shared ones).
      return matchLengthLine(report);
    default:
      // بی‌دل and the rest: no game-specific setting exists to name (only the four shared ones —
      // turn timer, sound, chat, deck — which are not worth a line before anyone has sat down).
      return [];
  }
}

function hokmLines(report: OnlineTableReport): string[] {
  const lines = matchLengthLine(report);
  // کوت only means something once the suit-less calls are ON — it is the scoring rule FOR them, and
  // saying «بدون کوت» about a table that cannot call سرس/نرس at all describes nothing.
  if (report.hokmModes === true) {
    lines.push("🃏 حاکم می‌تونه سرس / نرس بخونه");
    if (report.hokmModeKot === true) lines.push("💯 کوت روی این حکم‌ها هم حساب می‌شه");
  }
  return lines;
}

function matchLengthLine(report: OnlineTableReport): string[] {
  return typeof report.matchLength === "number"
    ? [`🎯 بازی تا ${faNum(report.matchLength)} دست`]
    : [];
}

/** شطرنج speaks in its own words: «آزاد» for an untimed table, else the raw `mm+ss` control. */
function chessTimeControlLabel(control: string): string {
  // The control arrives as a STRING on the wire, so it is the one shape line whose direction is not
  // ours to know — isolated for the same reason a display name is (see {@link isolate}).
  return control === "none" ? "بدون محدودیت زمانی" : `زمان‌بندی: ${isolate(faNum(control))}`;
}

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/** RIGHT-TO-LEFT MARK — a zero-width strong-RTL character. */
const RLM = "‏";
/** FIRST STRONG ISOLATE … POP DIRECTIONAL ISOLATE — a zero-width bidi bracket. */
const FSI = "⁨";
const PDI = "⁩";

/**
 * PIN ONE LINE TO RTL — an RLM at **both** ends, and the second one is the half that was missing.
 *
 * ⚠️ MEASURED, NOT REASONED — every claim below is a Chromium (ICU/UAX#9) reading of the visual
 * order of each line's grapheme clusters, compared against the same line in an explicit `dir=rtl`
 * box. Three client models were measured, because Telegram clients differ in how much text they
 * treat as one bidi paragraph: per-paragraph first-strong, whole-message first-strong, and a
 * FORCED LTR paragraph (an LTR-locale client). Results:
 *
 *   • BOTH first-strong models were already CORRECT for an emoji-led Persian line. UAX#9 P2 skips
 *     neutrals, and an emoji is ON — so the Persian is still the first STRONG character and the
 *     paragraph is RTL. The «a line starting with an emoji has no direction» story is only half
 *     the truth, and on its own it points at the wrong fix.
 *   • The FORCED-LTR model got EVERY line wrong — including lines that start with plain Persian
 *     and merely END in «.» or «!». That is the other half of the complaint («punctuation on the
 *     wrong side»), and no leading mark can reach it.
 *   • A LEADING RLM alone fixed 5 of 12 measured shapes. It cannot fix the rest by construction:
 *     with a level-0 paragraph, N2 resolves a TRAILING neutral run against `eos` (= L), so the
 *     «!», «.» or «👇» is left stranded at the right-hand end whatever the head of the line says.
 *   • RLM AT BOTH ENDS fixed everything except lines carrying a Latin name — see {@link isolate}.
 *   • `RLI…PDI` was measured too and is WORSE here (6 shapes wrong, incl. under first-strong);
 *     `RLE…PDF` also scores 0 but is a deprecated embedding that leaks if a line is ever split.
 *     RLM is inert, idempotent and already the house mark (`gate.ts`, `admin-panel.ts`).
 *
 * Idempotent, and a no-op on an empty line — a blank spacer line has nothing to order, and marking
 * it would put an invisible character where the copy wants nothing at all.
 */
export function rtlLine(line: string): string {
  if (line === "") return line;
  const head = line.startsWith(RLM) ? "" : RLM;
  const tail = line.endsWith(RLM) ? "" : RLM;
  return `${head}${line}${tail}`;
}

/** {@link rtlLine} over a multi-line block, line by line. The marks have to be PER LINE: each line
 *  is its own bidi paragraph (a hard break ends one), so one wrapper around the whole message
 *  cannot reach the lines below the first. */
export function rtlLines(text: string): string {
  return text.split("\n").map(rtlLine).join("\n");
}

/**
 * ISOLATE AN INTERPOLATED VALUE — a display name, a handle, a time control: anything that may be
 * Latin, digits, or a mix, inside a Persian sentence.
 *
 * ⚠️ THIS IS NOT BELT-AND-BRACES. A line that OPENS with a Latin display name («Reza برات جا نگه
 * داشته 🎴») was measured WRONG under all three client models, first-strong included — the name IS
 * the first strong character, so it makes the whole paragraph LTR and the Persian after it reads
 * backwards. `rtlLine` alone does not fix that line; `rtlLine` + this does (12 wrong shapes → 0).
 *
 * FSI rather than LRI: the value's own direction is whatever it is — a Persian nickname must stay
 * RTL inside its brackets, a Latin one LTR — and FSI is exactly «decide from the content».
 *
 * Zero-width, so it never shows; and it goes OUTSIDE any `<b>` so the bold entity still covers the
 * name alone. Empty in ⇒ empty out: bracketing nothing would add two characters for no reason.
 */
export function isolate(value: string): string {
  return value === "" ? value : `${FSI}${value}${PDI}`;
}

/**
 * PARSE ONE PUBLISHED TABLE REPORT, DEFENSIVELY — the single parser both readers use.
 *
 * The payload is untrusted input from a Redis key, so the object is rebuilt FIELD BY FIELD rather
 * than spread: a malformed or unexpected field costs its own line and never the row, and nothing
 * unvalidated reaches a caption.
 *
 * ⚠️ THIS IS ALSO WHERE A NEW FIELD SILENTLY DIES. A test that builds an `OnlineTableReport`
 * literal never crosses this boundary and stays green with the field dropped on the floor — which
 * is precisely how `matchLength` nearly shipped invisible once. Anything added to the report must
 * be added HERE and pinned by a test that goes through real JSON.
 *
 * It lives in `@bing/shared` because the BOT (scanning every table for the admin panel) and the API
 * (reading one table for an invite) both parse the same key, and two copies of a field-by-field
 * parser is two places to forget the same field.
 */
export function parseOnlineTableReport(raw: string | null | undefined): OnlineTableReport | null {
  if (raw === null || raw === undefined) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const o = parsed as Record<string, unknown>;
    const game = o["game"];
    const humans = o["humans"];
    const bots = o["bots"];
    const isPrivate = o["private"];
    const waiting = o["waiting"];
    const since = o["since"];
    if (typeof game !== "string" || game === "") return null;
    if (typeof humans !== "number" || typeof bots !== "number") return null;
    if (typeof isPrivate !== "boolean" || typeof waiting !== "boolean") return null;
    // `since` is null by design while a table is still waiting.
    if (since !== null && typeof since !== "number") return null;
    // `connected` may be absent if a game-server predating it is still running — fall back to the
    // seat count rather than dropping the row (a blank screen is worse than a slightly stale one).
    const conn = o["connected"];
    const connected = typeof conn === "number" ? conn : humans;
    const created = o["createdAt"];
    const length = o["matchLength"];
    const modes = o["hokmModes"];
    const kot = o["hokmModeKot"];
    const seats = o["seats"];
    const chessTime = o["chessTimeControl"];
    const host = o["hostName"];
    const hostId = o["hostAppUserId"];
    return {
      game,
      humans,
      connected,
      bots,
      private: isPrivate,
      waiting,
      since,
      ...(typeof created === "number" ? { createdAt: created } : {}),
      ...(typeof length === "number" ? { matchLength: length } : {}),
      ...(typeof modes === "boolean" ? { hokmModes: modes } : {}),
      ...(typeof kot === "boolean" ? { hokmModeKot: kot } : {}),
      ...(typeof seats === "number" ? { seats } : {}),
      ...(typeof chessTime === "string" && chessTime !== "" ? { chessTimeControl: chessTime } : {}),
      ...(typeof host === "string" && host !== "" ? { hostName: host } : {}),
      // An empty or non-string id is NOT a host — an absence reads as «not the host» to the api.
      ...(typeof hostId === "string" && hostId !== "" ? { hostAppUserId: hostId } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * IS THIS TABLE POSITIVELY OVER? — the ONE rule both invitation surfaces judge a dead table by.
 *
 * Two Redis keys answer two different questions: `online-table:<roomId>` (rewritten on every lobby
 * frame, 20s TTL) proves the table is ALIVE; `table-state:<roomId>` (12h) records its FATE. Only a
 * POSITIVE terminal fate licenses «the table is over» wording — and, in the api's case, licenses
 * WITHHOLDING the deep link. A missing key, an unrecognised value, a Redis blip or a table that
 * finished more than 12h ago all read `"unknown"`, and unknown must fall back to today's behaviour.
 * Absence of evidence is never evidence of absence: telling somebody their friend's table is gone
 * when it is not — or refusing them the door to a live one — is far worse than saying nothing.
 * (The bot's reply to a tapped link adds one reading of its own, a missing report beside a fresh
 * game-server heartbeat: `TableInviteInfo.gone` in its reader.)
 *
 * ⚠️ `waiting`/`playing` WITH NO LIVE REPORT IS DELIBERATELY NOT TERMINAL: that is the signature of
 * a wedged writer or a game-server recreate that killed the room without recording a fate, not of a
 * finished game.
 *
 * Lives here rather than in either service because the bot (private link) and the api (public «+»
 * PV) both decide it, and a second copy is a second answer.
 */
export interface TableVerdictInput {
  /** The live table's shape, or null when nothing is publishing it (gone, or unreadable). */
  report: OnlineTableReport | null;
  /** Its recorded fate, or `"unknown"` when we genuinely cannot tell. */
  state: TableState | "unknown";
}

export function isTableOver(info: TableVerdictInput): boolean {
  return (
    info.report === null &&
    (info.state === "finished" || info.state === "closed" || info.state === "expired")
  );
}

/**
 * CAN THIS PERSON ACTUALLY GET IN? — the ONE door verdict both invitation surfaces judge by.
 *
 * {@link isTableOver} answers half the question. The other half is the table that is very much
 * alive and still unenterable: every chair taken, or the game already dealt (a started room is
 * `lock()`ed, so `joinById` can only answer «is locked»). Offering somebody a door they cannot
 * walk through is worse than saying nothing, so the two are named separately:
 *
 *   • `over`    — a POSITIVE terminal fate and no live report. The table is finished/closed/expired.
 *   • `started` — a live report whose game has dealt. The room is locked; nobody new gets in.
 *   • `full`    — a live report still waiting, but with no free chair.
 *   • `open`    — a live report with room in it.
 *   • `unknown` — we could not tell. THIS IS THE FAIL-OPEN, and it is load-bearing: a Redis blip,
 *                 a link older than the 20s report key, or a table that finished more than 12h ago
 *                 all land here, and must keep today's behaviour (a normal invitation, door intact).
 *                 A missing key is never evidence that a friend's table is gone. (The bot's reader
 *                 alone adds a fresh heartbeat as that evidence: `TableInviteInfo.gone`.)
 *
 * ⚠️ ORDER: `over` is judged FIRST and requires `report === null`, so a live report always beats a
 * stale fate — the report key is rewritten every lobby frame and outlives nothing.
 */
export type TableDoor = "open" | "full" | "started" | "over" | "unknown";

export interface TableDoorOptions {
  /**
   * A MID-GAME SEAT-FILL invite — one minted for a specific chair a bot is holding, carrying its
   * own reconnection token. Such a table is started AND full BY DEFINITION; that is the whole
   * point of the invitation, so neither closes its door.
   */
  seatFill?: boolean;
}

export function tableDoor(info: TableVerdictInput, opts?: TableDoorOptions): TableDoor {
  if (isTableOver(info)) return "over";
  const report = info.report;
  if (report === null) return "unknown";
  if (opts?.seatFill === true) return "open";
  if (!report.waiting) return "started";
  // ⚠️ WHAT `waiting` MEANS, read off the RUNNING game-server rather than assumed: it publishes
  // `waiting = phase !== "playing"`, so the lobby, the team-arrangement stage AND the 10s pre-deal
  // beat all read `true`. That is what makes `full` reachable and correctly named — a table whose
  // last chair was taken a second ago is «پر شده», not «شروع شده».
  //
  // ⚠️ AND `humans` COUNTS SEATS, NOT CONNECTIONS (that is why `connected` exists beside it) — which
  // is the RIGHT number here: a chair held through a dropped player's reconnection grace is not a
  // chair anybody else can sit in.
  //
  // `seats` is optional on the wire (a game-server predating it may still be running), and an
  // unknown seat count cannot prove fullness — so it reads as room to spare, never as a shut door.
  if (typeof report.seats === "number" && report.humans + report.bots >= report.seats)
    return "full";
  return "open";
}

/**
 * The label above the table's settings block. SHARED because it was defined twice — once in the
 * bot's caption and once in the api's — which is one edit away from two different invitations.
 * «میز» rather than «بازی»: these are the table's own published settings.
 */
export const TABLE_DETAILS_HEADING = "<b>اطلاعات میز:</b>";

/**
 * THE LABEL ON THE BUTTON THAT OPENS THE TABLE — one label, both invitations.
 *
 * ⚠️ THE GLYPH IS IN THE LABEL, NOT an `icon_custom_emoji_id`: an INLINE button draws that icon on
 * the LEFT of a Persian label (i.e. AFTER the text) whatever bidi marks the label carries, so the
 * animated variant reads backwards. Measured on iPhone — see the RLM note in the bot's keyboard.ts.
 */
export const TABLE_JOIN_BUTTON_LABEL = "🎮 ورود به میز";

/**
 * The closing line of a LIVE invitation, shared by both surfaces. Both paths' button opens the same
 * thing — the table — so there is no reason a player could see for the two to word it differently.
 */
export const TABLE_INVITE_CTA = "بزن بریم سر میز 👇";

/**
 * The closing line of a SHUT invitation. There is no button on these messages by design (a door
 * that goes nowhere is worse than none), so it points at the bot's own menu instead — which every
 * recipient has: the bot path attaches it to this very message, and an api PV can only reach
 * somebody who has already started the bot.
 */
export const TABLE_SHUT_HINT =
  "این دعوت دیگه به جایی نمی‌ره — از منوی پایین می‌تونی یه بازی تازه شروع کنی.";

/**
 * WHY A TABLE REFUSES SOMEBODY, in the sentences the app says it in — so a player who taps a shared
 * link reads in the bot exactly what the app would have told them (card-d3, operator 2026-11-05).
 *
 * The room's three bars are the game-server's own refusals, which read them from here. The rest are
 * also `fa.invite.*` in the web, pinned equal by `apps/web/test/join-reason.test.ts`: that bundle was
 * left out of this push, and the pin is what stops the two drifting.
 */
export const TABLE_REFUSAL_FA = {
  finished: "این بازی تمام شده است",
  closed: "میزبان این میز را بست",
  expired: "این میز منقضی شد",
  started: "بازی این میز شروع شده است",
  full: "میز پر است",
  gone: "این میز دیگر در دسترس نیست",
  kicked: "میزبان تو را از این میز اخراج کرد",
  idle: "به‌خاطر بی‌حرکتی از این میز کنار گذاشته شدی و بازگشت به آن ممکن نیست",
  departed: "تو این میز را ترک کردی و دیگر نمی‌توانی برگردی",
} as const;
export type TableRefusal = keyof typeof TABLE_REFUSAL_FA;

/**
 * THE HEADLINE OF THE API'S INVITE PV for a table that is already shut. The bot's reply to a tapped
 * link no longer uses it — that says {@link TABLE_REFUSAL_FA} — and the operator chose to leave this
 * PV's wording as it is (card-d3, 2026-11-05).
 *
 * `gameFa` is embedded verbatim: the caller passes a value from its own fixed Persian game-name map,
 * never user input.
 */
export function tableShutHeadline(door: "over" | "full" | "started", gameFa: string): string {
  switch (door) {
    case "over":
      return `⌛️ <b>میز «${gameFa}» تموم شده</b>`;
    case "started":
      return `🔒 <b>بازی این میز شروع شده</b>`;
    case "full":
      return `🔒 <b>میز «${gameFa}» پر شده</b>`;
  }
}

/**
 * THE BODY OF A LIVE INVITATION — assembled in ONE place so the two surfaces cannot drift.
 *
 * `opening` is the caller's own, already-escaped lines (the bot names the HOST of a table somebody
 * shared a link to; the api names the person who INVITED you) — that is the only difference either
 * surface is allowed, because it is the only thing they genuinely know differently. Everything
 * below it — the heading, the settings blockquote, the closing line — is identical by construction.
 *
 * ⚠️ HEADING AND BLOCK LIVE OR DIE TOGETHER: a table with nothing to say (بی‌دل publishes no
 * settings) gets neither, never a label standing over an empty quote.
 *
 * ⚠️ `escape` is injected rather than imported: this module is transport-agnostic by design and
 * each service already owns its own HTML escaper. Today's shape lines are numbers and fixed
 * Persian, but `chessTimeControl` arrives as a string on the wire — the escape is what makes that
 * safe rather than lucky.
 */
export function tableInviteBody(
  opening: string,
  report: OnlineTableReport | null,
  escape: (value: string) => string,
): string {
  const lines = [rtlLines(opening)];
  const shape = report ? describeTableShape(report).map(escape).map(rtlLine) : [];
  if (shape.length > 0) {
    lines.push("", rtlLine(TABLE_DETAILS_HEADING), `<blockquote>${shape.join("\n")}</blockquote>`);
  }
  lines.push("", rtlLine(TABLE_INVITE_CTA));
  return lines.join("\n");
}

/** The body of a SHUT invitation: the caller's headline (plus any context only it knows), then the
 *  shared hint. Plain text — no photo, no glass button; see {@link TABLE_SHUT_HINT}. */
export function tableShutBody(opening: string): string {
  return `${rtlLines(opening)}\n\n${rtlLine(TABLE_SHUT_HINT)}`;
}
